import React from 'react';
import { styled } from '@mui/material/styles';
import ResourceTable, { IColumn } from '../../components/ResourceTable';

import { OrganisationAssignmentDto } from 'models/dtoTypes';
import Notice from '../../components/Notice';
import Link from 'components/Link';
import { organisationAssignmentFilterSchema } from './_smartGovernanceSchema';
import { DeleteOrganisationAssignment, GetAllOrganisationAssignments } from 'services/organisationAssignment';
import { Chip } from '@mui/material';
import generateNonce from 'utils/generateNonce';
import { date2str } from 'utils/date';
import { getKosmosReportLink, getSgReportLink } from 'utils/report';

const PREFIX = 'SmartGovernanceListPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    }
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export default function SmartGovernanceListPage(): JSX.Element {
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const [nonce, setNonce] = React.useState('');
  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);

  };

  const handleDeleteOrganisationAssignment = async (model: OrganisationAssignmentDto) => {
    try {
      await DeleteOrganisationAssignment(model.id);
      setNonce(generateNonce());
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: 'Cannot delete organisation assignment',
        severity: 'error',
      });
    }
  };


  const generateColumns = (): IColumn<OrganisationAssignmentDto>[] => [
    {
      label: 'Organisation',
      dataIndex: 'organisationId',
      sortIndex: 'organisationId',
      render: (model: OrganisationAssignmentDto) => {
        return (
          <Link to={`/organisations/${model.organisationId || ''}`} text={model.organisation?.name || '(Unknown)'}/>
        );
      }
    },
    {
      label: 'Assigner',
      dataIndex: 'assignerId',
      sortIndex: 'assignerId',
      render: (model: OrganisationAssignmentDto) => {
        return (
          model.assigner?.email || ''
        );
      }
    },
    {
      label: 'Assignee',
      dataIndex: 'assigneeId',
      sortIndex: 'assigneeId',
      render: (model: OrganisationAssignmentDto) => {
        return (
          model.assignee?.email || ''
        );
      }
    },
    {
      label: 'Kosmos Company',
      dataIndex: 'kosmosCompanyId',
      sortIndex: 'kosmosCompanyId',
      render: (model: OrganisationAssignmentDto) => {
        return (
          <Link to={getKosmosReportLink(model.kosmosCompanyId, model.organisationId)} text={model.kosmosCompanyName || '(Unknown)'}/>
        );
      }
    },
    {
      label: 'Kosmos Score',
      dataIndex: 'kosmosCompany',
      sortIndex: 'kosmosCompany',
      render: (model: OrganisationAssignmentDto) => {
        if (model.kosmosCompanyScore) {
          return (
            <Link to={getKosmosReportLink(model.kosmosCompanyId, model.organisationId)} text={model.kosmosCompanyScore.toString() || ''}/>
          );
        } else {
          return <></>;
        }
      }
    },
    {
      label: 'SG Score',
      dataIndex: 'manualCompany',
      sortIndex: 'manualCompany',
      render: (model: OrganisationAssignmentDto) => {
        if (model?.manualCompany?.esgScore) {
          return (
            <Link to={getSgReportLink(model.kosmosCompanyId, model.organisationId)} text={model?.manualCompany?.esgScore.toString() || ''}/>
          );
        } else {
          return <></>;
        }
      }
    },
    {
      label: 'SG Scored At',
      dataIndex: 'manualCompany_scoredAt',
      sortIndex: '',
      render: (model: OrganisationAssignmentDto) => {
        if (model?.manualCompany?.lastScoredAt) {
          return date2str(model?.manualCompany?.lastScoredAt, 'YYYY-MM-DD HH:mm:ss');
        } else {
          return <></>;
        }
      }
    },
    {
      label: 'Is Accepted',
      sortIndex: 'isAccepted',
      render: (model) => {
        const enabled = Boolean(model.isAccepted);
        return <Chip label={enabled ? 'Yes' : 'No'} color={enabled ? 'primary' : 'default'} />;
      }
    },
    {
      label: 'Action',
      actions: [
        {
          label: 'Remove',
          onClick: handleDeleteOrganisationAssignment
        },
      ],
    }
  ];

  return (
    <Root>
      <div className={classes.pageHeader}>
        <h2>Smart Governance Assignments</h2>
      </div>
      <ResourceTable
        key={nonce}
        filterSchema={organisationAssignmentFilterSchema}
        columns={generateColumns()}
        onFetch={GetAllOrganisationAssignments}
        defaultSort={{
          sortColumn: 'organisationId',
          isDesc: false
        }}
      />
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}
